import { BaseComponent } from '../../lib/base-component.js';
import { css, html, nothing } from '../../lib/lit.js';
import { renderMarkdown } from '../../lib/markdown.js';
import { MARKDOWN_STYLES } from '../../lib/markdown-styles.js';

function formatTime(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

// A poll as a member will see it on the community portal. The structure and
// classes mirror the portal's poll page (d3-community's community-poll-page),
// with the Markdown renderer and styles ported from what it uses, so staff see
// their intro and prompts render the way they will. Every control is disabled:
// this is a picture of the poll, not the poll.
class PollPreview extends BaseComponent {
    static properties = {
        /** @type {object} The poll in read shape (see draftToPreview). */
        poll: { attribute: false },
    };

    static styles = [
        MARKDOWN_STYLES,
        css`
            :host {
                display: block;
            }
        `,
    ];

    constructor() {
        super();
        this.poll = null;
    }

    _renderChoice(question) {
        const type = question.allowMultiple ? 'checkbox' : 'radio';
        const name = `q-${question.id}`;
        return html`
            ${question.options.map(
                (option) => html`
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type=${type}
                            name=${name}
                            id="o-${option.id}"
                            disabled
                        />
                        <label class="form-check-label" for="o-${option.id}">${option.label}</label>
                    </div>
                `,
            )}
            ${
                question.allowNotListed
                    ? html`
                          <div class="form-check">
                              <input
                                  class="form-check-input"
                                  type=${type}
                                  name=${name}
                                  id="nl-${question.id}"
                                  disabled
                              />
                              <label class="form-check-label" for="nl-${question.id}">Not listed</label>
                          </div>
                      `
                    : nothing
            }
            ${
                question.options.length < 2
                    ? html`<div class="form-text text-warning-emphasis">
                          A choice question needs at least two options before the poll can open.
                      </div>`
                    : nothing
            }
        `;
    }

    _renderText() {
        return html`
            <textarea class="form-control" rows="3" aria-label="Your answer" disabled></textarea>
            <div class="form-text">Only the D3 team reads written answers.</div>
        `;
    }

    _renderQuestion(question, index) {
        return html`
            <div class="mb-4" role="group" aria-labelledby="p-${question.id}">
                <div id="p-${question.id}" class="fw-semibold mb-2 d-flex gap-2">
                    <span class="text-muted">${index + 1}.</span>
                    <div class="markdown-body flex-grow-1">
                        ${question.prompt ? renderMarkdown(question.prompt) : html`<em class="text-muted">(no prompt yet)</em>`}
                    </div>
                </div>
                ${
                    question.kind === 'choice' && question.allowMultiple
                        ? html`<div class="form-text mt-0 mb-1">Pick any that apply.</div>`
                        : nothing
                }
                ${question.kind === 'text' ? this._renderText() : this._renderChoice(question)}
            </div>
        `;
    }

    render() {
        const poll = this.poll;
        if (!poll) return nothing;

        return html`
            <div class="card shadow-sm border-0 mb-4">
                <div class="card-body p-4">
                    <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        <i class="bi bi-bar-chart text-secondary"></i>
                        <span class="text-muted small">Poll</span>
                        <span class="badge text-bg-success">Open</span>
                        <span class="text-muted small">
                            ${poll.closesAt ? `Closes ${formatTime(poll.closesAt)}` : nothing}
                        </span>
                    </div>
                    <h1 class="h4 mb-3">${poll.title || html`<em class="text-muted">Untitled</em>`}</h1>
                    ${
                        poll.intro
                            ? html`<div class="markdown-body mb-4">${renderMarkdown(poll.intro)}</div>`
                            : nothing
                    }
                    ${
                        poll.questions.length === 0
                            ? html`<p class="text-muted"><em>No questions yet.</em></p>`
                            : poll.questions.map((question, index) =>
                                  this._renderQuestion(question, index),
                              )
                    }
                    <div class="d-flex gap-2 align-items-center flex-wrap">
                        <button class="btn btn-primary" type="button" disabled>Submit</button>
                        ${
                            poll.allowChanges
                                ? nothing
                                : html`<span class="text-muted small"
                                      >You can't change your answers once you submit.</span
                                  >`
                        }
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('d3-poll-preview', PollPreview);
